import React, { useEffect, useState, useCallback } from 'react';
import {
  Modal,
  Form,
  Input,
  Button,
  Row,
  Col,
  Typography,
  Space,
  InputNumber,
  Checkbox,
  Divider,
  Select,
  DatePicker,
  Radio,
  TimePicker,
  Collapse,
  Alert,
  message,
} from 'antd';
import { DeleteOutlined, EditOutlined, CheckOutlined } from '@ant-design/icons';
import { setZeroInvoicing, storeOrupdateLabTests, updateTestProcess } from '@app/api/labs.api';
import { notificationController } from '@app/controllers/notificationController';
import { StoreUpdateLabTestsResponse, UpdateTestProcessResponse } from '@app/models/labs/LabRequest';
import LabWorkflowButtons from './LabWorkflowButtons';
import dayjs from 'dayjs';
import useApiErrorHandler from '@app/components/Error/ErrorMsgHandling';
import { NumberInput } from '@app/components/header/dropdowns/settingsDropdown/settingsOverlay/nightModeSettings/NightTimeSlider/NightTimeSlider.styles';
import moment from 'moment';

const { Title, Text } = Typography;

interface TestDetailFormModalProps {
  visible: boolean;
  lab_id?: number | null;
  mainTest: any;
  mainTestId: string;
  deptId?: number | null;
  assigningParameters?: any[];
  labServices?: any[];
  selectedLabServices?: any[];
  technician_options?: any[];
  additionalInfo?: any;
  onClose: () => void;
  mode?: string | null;
  onTestUpdated?: (updatedTests: any[]) => void;
  status?: string | null;
  wf_actions?: any[];
  service_cat_id?: number | null;
  lab_samples?: any[];
  lab_type: string;
  divisionCode?: string;
}

const TestDetailsModal: React.FC<TestDetailFormModalProps> = ({
  visible,
  lab_id,
  mainTest,
  mainTestId,
  onClose,
  assigningParameters,
  labServices = [],
  selectedLabServices = [],
  deptId,
  technician_options,
  additionalInfo,
  mode,
  status,
  wf_actions = [],
  onTestUpdated,
  service_cat_id,
  lab_samples,
  lab_type,
  divisionCode,
}) => {
  const [form] = Form.useForm();
  const [isLoading, setLoading] = useState(false);
  const [addedSubTests, setAddedSubTests] = useState<Record<string, { id: string; quantity: number }>>({});
  const [currentMode, setCurrentMode] = useState<string | null>(mode || 'view');
  const [latestFormData, setLatestFormData] = useState<any>(null);
  const [latestSubTests, setLatestSubTests] = useState<Record<string, { id: string; quantity: number }>>({});
  const [workflowLoading, setWorkflowLoading] = React.useState(false);
  const [formInitialized, setFormInitialized] = useState(false);
  const [isFormValid, setIsFormValid] = useState(false);
  const selectedLabType = localStorage.getItem('selected_lab_type');
  const [sampleQuantity, setSampleQuantity] = useState<number>(1);
  const [sampleNames, setSampleNames] = useState<string[]>([`${divisionCode}/SAM/00000001`]);
  const [sampleValidationErrors, setSampleValidationErrors] = useState<string[]>([]);
  const handleErrorMessages = useApiErrorHandler();
  console.log('received lab samples data:', lab_samples);
  // Update the updateParentComponent function
  const updateParentComponent = useCallback(
    (updatedTestData: any) => {
      if (onTestUpdated && updatedTestData) {
        const testsArray = Array.isArray(updatedTestData) ? updatedTestData : [updatedTestData];

        const updatedTests = testsArray.map((testData) => ({
          lab_request_service_category_id: testData.id || testData.lab_request_service_category_id,
          id: testData.lab_service_category?.id || testData.lab_service_category_id,
          code: testData.lab_service_category?.code || testData.code,
          name: testData.lab_service_category?.name || testData.name,
          user_id: testData.user_id ? 'ADMIN' : 'Unassigned',
          sample_qty: testData.sample_qty || sampleQuantity,
          status:
            selectedLabType === 'DIRECT_SALES'
              ? testData.status_label ||
                testData.status || {
                  step_one: null,
                  step_two: null,
                  step_three: null,
                }
              : testData.test_statuses ||
                testData.status || {
                  step_one: null,
                  step_two: null,
                  step_three: null,
                },
          status_label_ds: testData.status_label || testData.status_label_ds,
          type: testData.lab_service_category?.type || testData.type,
        }));

        console.log('Updating parent with test data:', updatedTests);
        onTestUpdated(updatedTests);
      }
    },
    [onTestUpdated, selectedLabType, sampleQuantity],
  );

  useEffect(() => {
    if (!visible) {
      setFormInitialized(false);
      return;
    }

    if (!formInitialized) {
      setCurrentMode(mode || 'view');
    }

    // Only initialize form data once when modal opens
    if (!formInitialized) {
      if (mode === 'add') {
        setAddedSubTests({});
        form.resetFields();
        setSampleQuantity(1); // Reset sample quantity
        form.setFieldValue('sample_qty', 1);

        // Auto-select all sub tests for VACCINE_MANUFACTURING and DIRECT_SALES
        if (lab_type === 'VACCINE_MANUFACTURING' || lab_type === 'DIRECT_SALES') {
          const autoSelectedTests = labServices.reduce((acc, service) => {
            acc[service.id] = { id: service.id, quantity: 1 };
            return acc;
          }, {} as Record<string, { id: string; quantity: number }>);
          setAddedSubTests(autoSelectedTests);
        }

        setFormInitialized(true);
      } else if (mode === 'edit' || mode === 'view') {
        console.log('Processing edit/view mode with lab_samples:', lab_samples);
        const initialValues: any = {};

        let sample_qty = 1;
        let samples = [`${divisionCode}/SAM/00000001`];

        if (lab_samples && Array.isArray(lab_samples) && lab_samples.length > 0) {
          const labSampleData = lab_samples[0];
          sample_qty = labSampleData.sample_qty || 1;
          samples = labSampleData.samples || [];
          console.log('Setting from lab_samples (array):', { sample_qty, samples });
        } else if (lab_samples && !Array.isArray(lab_samples) && (lab_samples as any).sample_qty) {
          sample_qty = (lab_samples as any).sample_qty || 1;
          samples = (lab_samples as any).samples || [];
          console.log('Setting from lab_samples (object):', { sample_qty, samples });
        } else {
          console.log('No lab_samples data found, using defaults');
        }

        // Set sample data immediately
        setSampleQuantity(sample_qty);
        setSampleNames(samples);
        initialValues.sample_qty = sample_qty;

        // Handle assigning parameters
        if (latestFormData && latestFormData.assigningParameters) {
          latestFormData.assigningParameters.forEach((param: any) => {
            if (param.value) {
              initialValues[param.field_id] = param.value;
            }
          });
        } else if (assigningParameters) {
          assigningParameters.forEach((param) => {
            if (param.value) {
              initialValues[param.field_id] = param.value;
            }
          });
        }

        // Handle additional info
        if (latestFormData && latestFormData.additionalInfo) {
          if (latestFormData.additionalInfo.remarks) {
            initialValues.remarks = latestFormData.additionalInfo.remarks;
          }
          if (latestFormData.additionalInfo.technicianId) {
            initialValues.assign_tech = latestFormData.additionalInfo.technicianId;
          }
        } else if (additionalInfo) {
          if (additionalInfo.remarks) {
            initialValues.remarks = additionalInfo.remarks;
          }
          if (additionalInfo.technicianId) {
            initialValues.assign_tech = additionalInfo.technicianId;
          }
        }

        // Handle selected tests
        let selectedTests: Record<string, { id: string; quantity: number }> = {};
        if (Object.keys(latestSubTests).length > 0) {
          selectedTests = latestSubTests;
        } else if (selectedLabServices) {
          selectedLabServices.forEach((selectedService) => {
            selectedTests[selectedService.lab_service_id] = {
              id: selectedService.lab_service_id.toString(),
              quantity: parseFloat(selectedService.qty) || 1,
            };
          });
        }

        // Auto-select all sub tests for VACCINE_MANUFACTURING and DIRECT_SALES in edit mode
        if ((lab_type === 'VACCINE_MANUFACTURING' || lab_type === 'DIRECT_SALES') && mode === 'edit') {
          const autoSelectedTests = labServices.reduce((acc, service) => {
            const existingTest = selectedTests[service.id];
            acc[service.id] = existingTest || { id: service.id, quantity: 1 };
            return acc;
          }, {} as Record<string, { id: string; quantity: number }>);
          selectedTests = autoSelectedTests;
        }

        Object.keys(initialValues).forEach((key) => {
          const param = assigningParameters?.find((p) => p.field_id === key);
          if (!param?.input_type) return;

          const value = initialValues[key];
          if (!value) return;

          // Use moment instead of dayjs
          if (['DATE', 'DATE_TIME', 'TIME'].includes(param.input_type) && typeof value === 'string') {
            const formatMap = {
              DATE: 'YYYY-MM-DD',
              TIME: 'HH:mm',
              DATE_TIME: 'YYYY-MM-DD HH:mm',
            };

            const format = formatMap[param.input_type as keyof typeof formatMap];
            initialValues[key] = moment(value, format); // Changed from dayjs to moment
          }

          // Handle DATE_RANGE specifically
          if (param.input_type === 'DATE_RANGE' && Array.isArray(value) && value.length === 2) {
            initialValues[key] = [
              moment(value[0]).isValid() ? moment(value[0]) : null,
              moment(value[1]).isValid() ? moment(value[1]) : null,
            ].filter(Boolean);
          }
        });

        setTimeout(() => {
          setAddedSubTests(selectedTests);
          form.setFieldsValue(initialValues);
          console.log('Form values set:', initialValues);
        }, 0);

        setFormInitialized(true);
      }
    }
  }, [
    visible,
    mode,
    latestFormData,
    latestSubTests,
    assigningParameters,
    additionalInfo,
    selectedLabServices,
    formInitialized,
    lab_type,
    labServices,
    lab_samples,
  ]);

  const handleEditClick = () => {
    setCurrentMode('edit');
  };

  const handleActionClick = async (lab_id: number, deptId: number, service_cat_id: number, action: string) => {
    setWorkflowLoading(true);

    try {
      if (!lab_id || !deptId || !service_cat_id) {
        throw new Error('Missing required parameters: lab_id, deptId, or lab_request_service_category_id');
      }

      const response = (await updateTestProcess(lab_id, deptId, service_cat_id, {
        action,
      })) as UpdateTestProcessResponse;

      if (response && response.data) {
        notificationController.success({
          message: response.message || `Test updated successfully`,
        });

        if (response.data.lab_request_service_category) {
          const updatedCategory = response.data.lab_request_service_category;

          const updatedTestData = {
            lab_request_service_category_id: updatedCategory?.id || service_cat_id,
            id: updatedCategory?.id || service_cat_id,
            code: updatedCategory.lab_service_category?.code || '',
            name: updatedCategory.lab_service_category?.name || '',
            user_id: updatedCategory.user_id ? 'ADMIN' : 'Unassigned',
            sample_qty: updatedCategory.sample_qty || sampleQuantity,
            status:
              selectedLabType === 'DIRECT_SALES'
                ? updatedCategory.status_label || {
                    step_one: null,
                    step_two: null,
                    step_three: null,
                  }
                : updatedCategory.test_statuses || {
                    step_one: null,
                    step_two: null,
                    step_three: null,
                  },
            status_label_ds: updatedCategory.status_label || updatedCategory.status_label_ds,
            type: updatedCategory.lab_service_category?.type || '',
          };

          updateParentComponent(updatedTestData);

          setTimeout(() => {
            handleModalClose();
          }, 200);
        } else {
          handleModalClose();
        }
      }
    } catch (err: any) {
      console.error('Workflow action error:', err);
      handleErrorMessages(err);
    } finally {
      setWorkflowLoading(false);
    }
  };

  const handleZeroInvoicingClick = async (action: string) => {
    try {
      setWorkflowLoading(true);

      const zeroInvoicingResponse = await setZeroInvoicing(lab_id!, deptId!, service_cat_id!, action);

      if (zeroInvoicingResponse && (zeroInvoicingResponse.status === 201 || zeroInvoicingResponse.data)) {
        notificationController.success({ message: 'Test Approved as Zero Invoice' });

        if (zeroInvoicingResponse.data && zeroInvoicingResponse.data.lab_request_service_category) {
          const updatedCategory = zeroInvoicingResponse.data.lab_request_service_category;

          const updatedTestData = {
            lab_request_service_category_id: updatedCategory?.id || service_cat_id,
            id: updatedCategory?.id || service_cat_id,
            code: updatedCategory.lab_service_category?.code || '',
            name: updatedCategory.lab_service_category?.name || '',
            user_id: updatedCategory.user_id ? 'ADMIN' : 'Unassigned',
            sample_qty: updatedCategory.sample_qty || sampleQuantity,
            status:
              selectedLabType === 'DIRECT_SALES'
                ? updatedCategory.status_label || {
                    step_one: null,
                    step_two: null,
                    step_three: null,
                  }
                : updatedCategory.test_statuses || {
                    step_one: null,
                    step_two: null,
                    step_three: null,
                  },
            status_label_ds: updatedCategory.status_label || updatedCategory.status_label_ds,
            type: updatedCategory.lab_service_category?.type || '',
          };

          console.log('Updating parent with zero invoicing response:', updatedTestData);
          updateParentComponent(updatedTestData);

          setTimeout(() => {
            handleModalClose();
          }, 200);
        } else {
          setTimeout(() => {
            handleModalClose();
          }, 200);
        }
      } else {
        handleErrorMessages(zeroInvoicingResponse?.error);
        console.error('Zero invoicing API failed:', zeroInvoicingResponse);
      }
    } catch (err: any) {
      console.error('Error in zero invoicing process:', err);
      handleErrorMessages(err);
    } finally {
      setWorkflowLoading(false);
    }
  };

  const handleToggleSubTest = (service: any, checked: boolean) => {
    if (currentMode === 'view') return;

    // Disable toggle for VACCINE_MANUFACTURING and DIRECT_SALES
    if (lab_type === 'VACCINE_MANUFACTURING' || lab_type === 'DIRECT_SALES') return;

    if (checked) {
      setAddedSubTests((prev) => ({
        ...prev,
        [service.id]: {
          id: service.id,
          quantity: lab_type === 'VACCINE_MANUFACTURING' || lab_type === 'DIRECT_SALES' ? 1 : sampleQuantity,
        },
      }));
    } else {
      setAddedSubTests((prev) => {
        const copy = { ...prev };
        delete copy[service.id];
        return copy;
      });
    }
  };
  const handleQuantityChange = (serviceId: string, qty: number | null) => {
    if (currentMode === 'view') return;
    if (!qty || qty < 1) return;

    setAddedSubTests((prev) => ({
      ...prev,
      [serviceId]: { id: serviceId, quantity: qty },
    }));
  };

  const generateSampleCode = (sequenceNumber: number): string => {
    const paddedNumber = sequenceNumber.toString().padStart(8, '0');
    return `${divisionCode}/SAM/${paddedNumber}`;
  };

  // Helper function to find the next available sequence number based on field position
  const findNextSequenceNumber = (startIndex: number): number => {
    return startIndex + 1;
  };

  const validateSampleNames = (): string[] => {
    const errors: string[] = [];
    sampleNames.forEach((name, index) => {
      if (!name || name.trim() === '') {
        errors.push(`Sample ${index + 1} name is required`);
      }
    });
    return errors;
  };

  const handleSampleQuantityChange = (value: number | null) => {
    if (currentMode === 'view') return;

    const newQuantity = value || 1;

    setSampleQuantity(newQuantity);
    form.setFieldValue('sample_qty', newQuantity);

    const newSampleNames = Array.from({ length: newQuantity }, (_, index) => {
      // Keep existing names if they exist and are not empty
      if (sampleNames && sampleNames[index] && sampleNames[index].trim()) {
        return sampleNames[index];
      }
      return '';
    });

    setSampleNames(newSampleNames);

    setSampleValidationErrors([]);

    // Update all selected subtest quantities to match sample quantity
    if (lab_type !== 'VACCINE_MANUFACTURING' && lab_type !== 'DIRECT_SALES') {
      setAddedSubTests((prev) => {
        const updated = { ...prev };
        Object.keys(updated).forEach((serviceId) => {
          updated[serviceId] = { ...updated[serviceId], quantity: newQuantity };
        });
        return updated;
      });
    }
  };

  const handleSampleNameChange = (index: number, value: string) => {
    const updatedNames = [...sampleNames];
    updatedNames[index] = value;
    setSampleNames(updatedNames);

    // Clear validation errors
    setSampleValidationErrors([]);
  };

  const handleAutoFillEmptyFields = () => {
    const updatedNames = sampleNames.map((name, index) => {
      if (!name || name.trim() === '') {
        return generateSampleCode(findNextSequenceNumber(index));
      }
      return name;
    });
    setSampleNames(updatedNames);
    setSampleValidationErrors([]);
  };

  const handleClearAll = () => {
    setSampleNames(new Array(sampleQuantity).fill(''));
    setSampleValidationErrors([]);
  };

  // Validate before form submission
  const handleFormSubmit = (values: any) => {
    const errors = validateSampleNames();
    setSampleValidationErrors(errors);

    if (errors.length > 0) {
      message.error('Please fill in all sample names before submitting');
      return;
    }

    const transformedValues = { ...values };
    (assigningParameters ?? []).forEach((param) => {
      if (param.input_type === 'DATE_RANGE' && Array.isArray(values[param.field_id])) {
        transformedValues[param.field_id] = values[param.field_id].map((date: any) => date?.toISOString?.() || date);
      }
    });

    handleFinish(transformedValues);
  };

  // Check if there are empty sample names
  const hasEmptySampleNames = sampleNames.some((name) => !name || name.trim() === '');
  const namedSamplesCount = sampleNames.filter((name) => name && name.trim()).length;

  const handleSelectAll = (checked: boolean) => {
    if (currentMode === 'view') return;

    if (lab_type === 'VACCINE_MANUFACTURING' || lab_type === 'DIRECT_SALES') return;

    if (checked) {
      const all = labServices.reduce((acc, service) => {
        acc[service.id] = {
          id: service.id,
          quantity: lab_type === 'VACCINE_MANUFACTURING' || lab_type === 'DIRECT_SALES' ? 1 : sampleQuantity,
        };
        return acc;
      }, {} as Record<string, { id: string; quantity: number }>);
      setAddedSubTests(all);
    } else {
      setAddedSubTests({});
    }
  };

  const handleFinish = async (values: any) => {
    if (currentMode === 'view') return;

    // Validate that at least one sub test is selected
    if (Object.keys(addedSubTests).length === 0) {
      notificationController.error({
        message: 'Validation Error',
        description: 'Select at least 1 sub test to continue',
      });
      return;
    }

    const assigningParams = Object.fromEntries(
      (assigningParameters || []).map((param) => [param.field_id, values[param.field_id] || '']),
    );

    const labRequestServiceCategoryId =
      selectedLabServices?.length > 0 ? selectedLabServices[0].lab_request_service_category_id : null;

    const allSubTests = Object.values(addedSubTests).map((subtest) => {
      const existing = selectedLabServices.find((s) => String(s.lab_service_id) === String(subtest.id));
      const labRequestServiceId = existing ? existing.id : null;
      return {
        lab_request_service_id: labRequestServiceId,
        lab_service_id: subtest.id,
        qty: subtest.quantity,
      };
    });

    const testData = {
      lab_service_category_id: Number(mainTestId),
      user_id: values.assign_tech || '',
      description: values.remarks || '',
      sample_qty: values.sample_qty || sampleQuantity,
      samples: sampleNames,
      ...(currentMode === 'edit' &&
        labRequestServiceCategoryId && {
          lab_request_service_category_id: labRequestServiceCategoryId,
        }),
      assigning_parameters: assigningParams,
      lab_request_services: allSubTests,
    };

    try {
      setLoading(true);
      if (!lab_id || !deptId) {
        throw new Error('Missing lab request ID or department ID');
      }

      //console.log('submitted testData:', testData);

      const res = await storeOrupdateLabTests(lab_id, deptId, testData);
      const response = res as any;

      if (response && response.data && response.data.lab_request_service_category) {
        const updatedCategory = response.data.lab_request_service_category;

        const latestData = {
          assigningParameters: updatedCategory.assigning_parameters || [],
          additionalInfo: {
            remarks: updatedCategory.description || '',
            technicianId: updatedCategory.user_id || null,
            sampleQuantity: updatedCategory.sample_qty || sampleQuantity,
          },
        };
        setLatestFormData(latestData);

        const latestSubTests: Record<string, { id: string; quantity: number }> = {};
        if (updatedCategory.lab_request_services) {
          updatedCategory.lab_request_services.forEach((service: any) => {
            latestSubTests[service.lab_service_id] = {
              id: service.lab_service_id.toString(),
              quantity: parseFloat(service.qty) || 1,
            };
          });
        }
        setLatestSubTests(latestSubTests);

        updateParentComponent(updatedCategory);
      }

      if (response && response.lab_request && response.lab_request.lab_request_departments) {
        const allUpdatedTests: any[] = [];
        const currentDept = response.lab_request.lab_request_departments.find((dept: any) => dept.id === deptId);

        if (currentDept && currentDept.lab_request_service_categories) {
          currentDept.lab_request_service_categories.forEach((serviceCategory: any) => {
            allUpdatedTests.push({
              lab_request_service_category_id: serviceCategory.id,
              id: serviceCategory.lab_service_category.id,
              code: serviceCategory.lab_service_category.code,
              name: serviceCategory.lab_service_category.name,
              user_id: serviceCategory.user_id ? 'ADMIN' : 'Unassigned',
              sample_qty: serviceCategory.sample_qty || sampleQuantity,
              status:
                selectedLabType === 'DIRECT_SALES'
                  ? serviceCategory.status_label || {
                      step_one: null,
                      step_two: null,
                      step_three: null,
                    }
                  : serviceCategory.test_statuses || {
                      step_one: null,
                      step_two: null,
                      step_three: null,
                    },
              status_label_ds: serviceCategory.status_label || serviceCategory.status_label_ds,
              type: serviceCategory.lab_service_category.type,
            });
          });
        }

        if (onTestUpdated && allUpdatedTests.length > 0) {
          console.log('Updating parent with all tests:', allUpdatedTests);
          onTestUpdated(allUpdatedTests);
        }
      }

      const result = response as { message: string };
      notificationController.success({ message: result.message || 'Test saved successfully' });

      if (currentMode === 'edit') {
        setTimeout(() => {
          setCurrentMode('view');
        }, 100);
      } else {
        handleModalClose();
      }
    } catch (err) {
      console.error('Error submitting test data:', err);
      handleErrorMessages(err);
    } finally {
      setLoading(false);
    }
  };

  // Update sub tests when latest data changes and in view mode
  useEffect(() => {
    if (visible && currentMode === 'view' && Object.keys(latestSubTests).length > 0) {
      setAddedSubTests(latestSubTests);
    }
  }, [latestSubTests, visible, currentMode]);

  const handleModalClose = () => {
    form.resetFields();
    setAddedSubTests({});
    setCurrentMode(mode || 'view');
    setLatestFormData(null);
    setLatestSubTests({});
    setFormInitialized(false);
    setSampleQuantity(1);
    onClose();
  };

  const getServicesToDisplay = () => {
    if (currentMode === 'view') {
      const subTestsToCheck = Object.keys(latestSubTests).length > 0 ? latestSubTests : addedSubTests;
      return labServices.filter(
        (service) =>
          Object.keys(subTestsToCheck).includes(service.id.toString()) ||
          selectedLabServices?.some((selected) => selected.lab_service_id === service.id),
      );
    } else {
      return labServices;
    }
  };

  const servicesToDisplay = getServicesToDisplay();
  const isReadOnly = currentMode === 'view';

  const renderFieldComponent = (param: any, isReadOnly: boolean) => {
    const commonProps = { style: { width: '100%' }, disabled: isReadOnly };
    const { RangePicker } = DatePicker;
    const maxRule = param.rules?.find((rule: string) => rule.startsWith('max:'));
    const maxLength = maxRule ? parseInt(maxRule.split(':')[1]) : undefined;
    const createSafeMomentRange = (value: any): [moment.Moment, moment.Moment] | undefined => {
      console.log('Processing DATE_RANGE value:', value);

      if (!value || !Array.isArray(value) || value.length !== 2) {
        return undefined;
      }

      try {
        // Handle different value types
        const dates = value.map((dateValue: any) => {
          // If it's already a moment object, return it
          if (moment.isMoment(dateValue)) {
            return dateValue;
          }

          // If it's a dayjs object, convert to moment
          if (dateValue && typeof dateValue === 'object' && dateValue.$d) {
            return moment(dateValue.$d);
          }

          // If it's a string or Date object, create moment
          return moment(dateValue);
        });

        // Validate both dates
        if (!dates[0].isValid() || !dates[1].isValid()) {
          console.warn('Invalid dates in range:', value);
          return undefined;
        }

        console.log('Created valid moment range:', dates);
        return [dates[0], dates[1]] as [moment.Moment, moment.Moment];
      } catch (error) {
        console.error('Error creating moment range:', error);
        return undefined;
      }
    };

    const typeMap = {
      STRING: <Input readOnly={isReadOnly} />,
      NUMBER: <InputNumber {...commonProps} />,
      TEXTAREA: (
        <Input.TextArea rows={2} readOnly={isReadOnly} {...commonProps} showCount={true} maxLength={maxLength} />
      ),
      DATE: <DatePicker {...commonProps} format="YYYY-MM-DD" />,
      TIME: <TimePicker {...commonProps} format="HH:mm" />,
      DATE_TIME: <DatePicker {...commonProps} showTime format="YYYY-MM-DD HH:mm" />,
      DATE_RANGE: <RangePicker {...commonProps} format="YYYY-MM-DD" value={createSafeMomentRange(param.value)} />,

      SELECTION: (
        <Select {...commonProps}>
          {(param.options || []).map((option: string, index: number) => (
            <Select.Option key={index} value={option}>
              {option}
            </Select.Option>
          ))}
        </Select>
      ),
      CHECKBOX: <Checkbox disabled={isReadOnly} />,
      RADIO: (
        <Radio.Group disabled={isReadOnly}>
          {(param.options || []).map((option: string, index: number) => (
            <Radio key={index} value={option}>
              {option}
            </Radio>
          ))}
        </Radio.Group>
      ),
      // Fallback
      DEFAULT: <Input readOnly={isReadOnly} />,
    };

    return typeMap[param.input_type as keyof typeof typeMap] || typeMap.DEFAULT;
  };

  const getValidationRules = (param: any, isReadOnly: boolean) => {
    const rules: any[] = [];

    (param.rules || []).forEach((rule: string) => {
      if (rule === 'required') {
        rules.push({ required: true, message: `${param.name} is required` });
      } else if (rule === 'string') {
        rules.push({ type: 'string', message: `${param.name} must be a string` });
      } else if (rule.startsWith('max:')) {
        const maxLength = parseInt(rule.split(':')[1]);
        if (param.input_type === 'TEXTAREA') {
          rules.push({
            validator: (_: any, value: string) => {
              if (!value || value.length <= maxLength) {
                return Promise.resolve();
              }
              return Promise.reject(new Error(`Maximum ${maxLength} characters allowed (current: ${value.length})`));
            },
          });
        }
      }
    });

    return isReadOnly ? [] : rules;
  };

  return (
    <Modal
      visible={visible}
      title={`${mainTest} ${currentMode ? `(${currentMode.toUpperCase()})` : ''}`}
      // title={mainTest}
      onCancel={handleModalClose}
      footer={null}
      centered
      width={900}
      bodyStyle={{ maxHeight: 900, overflowY: 'auto', paddingRight: 16 }}
    >
      <LabWorkflowButtons
        wf_actions={wf_actions}
        currentMode={currentMode}
        onEditClick={handleEditClick}
        onActionClick={(action) => handleActionClick(lab_id!, deptId!, service_cat_id!, action)}
        onZeroInvoicingClick={handleZeroInvoicingClick}
        loading={workflowLoading}
        disabled={isLoading}
        status={status}
      />

      <Form form={form} layout="vertical" onFinish={handleFormSubmit}>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="assign_tech" label="Assigned Technician">
              {isReadOnly ? (
                <div style={{ padding: '6px 11px', border: '1px solid #d9d9d9', borderRadius: 6, minHeight: 32 }}>
                  {technician_options?.find((tech) => tech.value === form.getFieldValue('assign_tech'))?.label || '-'}
                </div>
              ) : (
                <Select placeholder="Select a technician">
                  {technician_options?.map((tech) => (
                    <Select.Option key={tech.value} value={tech.value}>
                      {tech.label}
                    </Select.Option>
                  ))}
                </Select>
              )}
            </Form.Item>
          </Col>
          {(assigningParameters ?? []).map((param) => {
            return (
              <Col span={12} key={param.field_id}>
                <Form.Item
                  name={param.field_id}
                  label={param.uom?.trim() ? `${param.name} (${param.uom})` : param.name}
                  rules={getValidationRules(param, isReadOnly)}
                  valuePropName={param.input_type === 'CHECKBOX' ? 'checked' : 'value'}
                >
                  {renderFieldComponent(param, isReadOnly)}
                </Form.Item>
              </Col>
            );
          })}

          {selectedLabType !== 'VACCINE_MANUFACTURING' && selectedLabType !== 'DIRECT_SALES' && (
            <Col span={24}>
              <Form.Item name="sample_qty" label="Sample Quantity">
                <InputNumber
                  min={1}
                  onChange={handleSampleQuantityChange}
                  style={{ width: '200px' }}
                  readOnly={isReadOnly}
                />
              </Form.Item>

              {sampleQuantity >= 1 && (
                <Collapse ghost style={{ marginTop: 8, border: '1px solid #f0f0f0', borderRadius: 6 }}>
                  <Collapse.Panel
                    header={
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>Sample Names</span>
                        <span
                          style={{
                            color: hasEmptySampleNames ? '#ff4d4f' : '#52c41a',
                            fontSize: '12px',
                            fontWeight: hasEmptySampleNames ? 'bold' : 'normal',
                          }}
                        >
                          {namedSamplesCount}/{sampleQuantity} named
                          {hasEmptySampleNames && ' - Missing names!'}
                        </span>
                      </div>
                    }
                    key="1"
                  >
                    <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                      {/* Individual Validation Errors */}
                      {sampleValidationErrors.length > 0 && (
                        <Alert
                          message="Please fill in the following sample names:"
                          description={sampleValidationErrors.join(', ')}
                          type="error"
                          showIcon
                          style={{ marginBottom: 12 }}
                        />
                      )}

                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                          gap: 8,
                          padding: '8px 0',
                        }}
                      >
                        {sampleNames.map((name, index) => {
                          const isEmpty = !name || name.trim() === '';
                          return (
                            <div key={index} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <Text
                                style={{
                                  minWidth: '30px',
                                  fontSize: '12px',
                                  color: '#666',
                                  textAlign: 'right',
                                }}
                              >
                                {index + 1}.
                              </Text>
                              <Input
                                size="small"
                                placeholder={generateSampleCode(index + 1)}
                                value={name}
                                onChange={(e) => handleSampleNameChange(index, e.target.value)}
                                readOnly={isReadOnly}
                                style={{
                                  flex: 1,
                                  borderColor: isEmpty && !isReadOnly ? '#ff4d4f' : undefined,
                                  backgroundColor: isEmpty && !isReadOnly ? '#fff2f0' : undefined,
                                }}
                                status={isEmpty && !isReadOnly ? 'error' : undefined}
                              />
                            </div>
                          );
                        })}
                      </div>

                      {!isReadOnly && (
                        <div
                          style={{
                            marginTop: 12,
                            padding: 8,
                            background: '#fafafa',
                            borderRadius: 4,
                            display: 'flex',
                            gap: 8,
                            alignItems: 'center',
                            flexWrap: 'wrap',
                          }}
                        >
                          <Text style={{ fontSize: '12px', color: '#666' }}>Bulk actions:</Text>
                          <Button size="small" onClick={handleAutoFillEmptyFields} type="primary" ghost>
                            AutoFill Empty Fields
                          </Button>
                          <Button size="small" onClick={handleClearAll} danger>
                            Clear All
                          </Button>
                        </div>
                      )}
                    </div>
                  </Collapse.Panel>
                </Collapse>
              )}
            </Col>
          )}
          <br />
          <Col span={24}>
            <Form.Item name="remarks" label="Remarks">
              <Input.TextArea rows={2} readOnly={isReadOnly} />
            </Form.Item>
          </Col>
        </Row>

        <Divider />
        <div
          style={{
            marginBottom: 15,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Title level={5} style={{ margin: 0 }}>
            {lab_type === 'VACCINE_MANUFACTURING'
              ? 'Vaccines'
              : lab_type === 'DIRECT_SALES'
              ? 'Services'
              : currentMode === 'view'
              ? 'Selected Sub Tests'
              : 'Sub Tests'}
          </Title>

          {currentMode !== 'view' && !(lab_type === 'VACCINE_MANUFACTURING' || lab_type === 'DIRECT_SALES') && (
            <Checkbox
              checked={servicesToDisplay.length > 0 && Object.keys(addedSubTests).length === servicesToDisplay.length}
              onChange={(e) => handleSelectAll(e.target.checked)}
            >
              Select All
            </Checkbox>
          )}
        </div>

        {servicesToDisplay.length > 0 && (
          <>
            {servicesToDisplay.map((service) => {
              const isAdded = !!addedSubTests[service.id];
              const qty = addedSubTests[service.id]?.quantity || 1;
              const isAutoSelected = lab_type === 'VACCINE_MANUFACTURING' || lab_type === 'DIRECT_SALES';

              return (
                <div key={service.id} style={{ marginBottom: 10 }}>
                  <Checkbox
                    checked={isAdded}
                    onChange={(e) => handleToggleSubTest(service, e.target.checked)}
                    style={{ fontWeight: 500 }}
                    disabled={isReadOnly || isAutoSelected}
                  >
                    <span style={{ fontWeight: '600', fontSize: '18px' }}>{service.name}</span>
                    {service.uom && ` (${service.uom})`}
                  </Checkbox>

                  {isAdded && (
                    <>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginTop: 6,
                          marginLeft: 24,
                          background: '#fafafa',
                          padding: '6px 10px',
                          borderRadius: 4,
                          fontSize: '16px',
                        }}
                      >
                        <Text>
                          {service.name} {service.uom && `(${service.uom})`}
                        </Text>
                        <Space>LKR{service.unit_price}</Space>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontWeight: 'bold' }}>x</span>
                          <InputNumber
                            min={1}
                            value={qty}
                            onChange={(val) => handleQuantityChange(service.id, val)}
                            style={{ width: 60 }}
                            size="small"
                            readOnly={isReadOnly}
                            disabled={
                              !(lab_type === 'VACCINE_MANUFACTURING' || lab_type === 'DIRECT_SALES') && !isReadOnly
                            }
                          />
                          <Text>Nos</Text>
                          {!isReadOnly && !isAutoSelected && (
                            <Button
                              type="text"
                              icon={<DeleteOutlined />}
                              size="small"
                              danger
                              onClick={() => handleToggleSubTest(service, false)}
                            />
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </>
        )}

        <Divider />
        <div style={{ display: 'flex', justifyContent: 'flex-start', gap: 8 }}>
          <Button onClick={handleModalClose} size="small">
            {currentMode === 'view' ? 'Close' : 'Cancel'}
          </Button>
          {currentMode !== 'view' && (
            <Button type="primary" htmlType="submit" size="small" loading={isLoading}>
              {currentMode === 'edit' ? 'Update' : 'Save'}
            </Button>
          )}
        </div>
      </Form>
    </Modal>
  );
};

export default TestDetailsModal;
